// src/components/crm/RelanceModal.jsx
import React, { useState, useEffect } from 'react';
import '../../styles/crm/relances.css';

/**
 * Modal générique de création / reprogrammation d'une relance.
 *
 * Props :
 *   isOpen          {bool}     afficher le modal
 *   onClose         {func}     fermer sans sauvegarder
 *   onSave          {func}     appelé avec { dateRelance, commentaire, formationIds }
 *   initialDate     {string}   pré-remplir la date  (optionnel)
 *   title           {string}   titre du modal       (optionnel)
 *   loading         {bool}     désactive le bouton pendant la requête
 *   formations      {Array}    liste { id, intitule } des formations du prospect
 *   defaultFormId   {number}   ID de la formation à pré-sélectionner
 */
export default function RelanceModal({
  isOpen,
  onClose,
  onSave,
  initialDate    = '',
  title          = 'Programmer une relance',
  loading        = false,
  formations     = [],   // ← formations souhaitées du prospect
  defaultFormId  = null,
}) {
  // ✅ DEBUG : Log des données reçues
  useEffect(() => {
    if (isOpen) {
      console.log('🔍 RelanceModal - isOpen = true');
      console.log('   formations reçues:', formations);
      console.log('   formations.length:', formations.length);
      if (formations.length > 0) {
        console.log('   première formation:', formations[0]);
        console.log('   typeof formations[0].id:', typeof formations[0].id);
      }
    }
  }, [isOpen, formations]);

  const [dateRelance,   setDateRelance]   = useState('');
  const [commentaire,   setCommentaire]   = useState('');
  const [formationIds,  setFormationIds]  = useState([]);  // ← Array de IDs sélectionnés
  const [error,         setError]         = useState('');

  // Réinitialise le formulaire à chaque ouverture
  useEffect(() => {
    if (isOpen) {
      setDateRelance(initialDate || '');
      setCommentaire('');
      
      // Pré-sélectionner les formations
      // ✅ NORMALISATION : Convertir tous les IDs en string pour comparaison cohérente
      if (defaultFormId) {
        setFormationIds([String(defaultFormId)]);
        console.log('   → Pré-sélection: defaultFormId =', defaultFormId);
      } else if (formations.length > 0) {
        setFormationIds([String(formations[0].id)]);
        console.log('   → Pré-sélection: première formation =', formations[0].id);
      } else {
        setFormationIds([]);
        console.log('   → Aucune formation à pré-sélectionner');
      }
      
      setError('');
    }
  }, [isOpen, initialDate, defaultFormId, formations]);

  // ── Gérer la sélection/désélection des checkboxes ──────────────────
  const handleFormationToggle = (formationId) => {
    const normalizedId = String(formationId);
    console.log('🔄 Toggle formation:', normalizedId);
    
    setFormationIds((prev) => {
      if (prev.includes(normalizedId)) {
        // Désélectionner
        console.log('   → Désélection');
        return prev.filter((id) => id !== normalizedId);
      } else {
        // Sélectionner
        console.log('   → Sélection');
        return [...prev, normalizedId];
      }
    });
  };

  const handleSave = () => {
    if (!dateRelance) {
      setError('La date de relance est obligatoire.');
      return;
    }
    
    console.log('💾 Sauvegarde - formations sélectionnées (string):', formationIds);
    
    // ✅ Convertir les strings en nombres pour l'API
    const numericFormationIds = formationIds.map(id => {
      const num = parseInt(id, 10);
      return isNaN(num) ? null : num;
    }).filter(id => id !== null);
    
    console.log('   → Envoi à l\'API (numeric):', numericFormationIds);
    
    setError('');
    onSave({
      dateRelance,
      commentaire,
      formationIds: numericFormationIds,  // ← Tableau de IDs sélectionnés (nombres)
    });
  };

  if (!isOpen) return null;

  return (
    <div className="relance-modal-overlay" onClick={onClose}>
      <div
        className="relance-modal-card"
        onClick={(e) => e.stopPropagation()}
      >
        {/* ── Header ─────────────────────────────────────────── */}
        <div className="relance-modal-header">
          <div className="relance-modal-icon">
            <i className="fa-solid fa-bell"></i>
          </div>
          <h3 className="relance-modal-title">{title}</h3>
          <button className="relance-modal-close" onClick={onClose}>
            <i className="fa-solid fa-xmark"></i>
          </button>
        </div>

        {/* ── Body ───────────────────────────────────────────── */}
        <div className="relance-modal-body">
          {error && (
            <div className="relance-error-banner">
              <i className="fa-solid fa-triangle-exclamation"></i> {error}
            </div>
          )}

          {/* Formations — Checkboxes */}
          {formations.length > 0 ? (
            <div className="relance-field">
              <label className="relance-label">
                <i className="fa-solid fa-graduation-cap"></i> Formations
                <span className="optional-tag">optionnel</span>
              </label>
              <div className="formations-checkboxes">
                {formations.map((f) => {
                  const normalizedFid = String(f.id);
                  const isChecked = formationIds.includes(normalizedFid);
                  
                  return (
                    <label key={f.id} className="checkbox-item">
                      <input
                        type="checkbox"
                        checked={isChecked}
                        onChange={() => handleFormationToggle(f.id)}
                      />
                      <span className="checkbox-label">{f.intitule}</span>
                    </label>
                  );
                })}
              </div>
              {formationIds.length > 0 && (
                <div className="selected-formations">
                  <small>
                    <i className="fa-solid fa-check"></i>{' '}
                    {formationIds.length} formation{formationIds.length > 1 ? 's' : ''} sélectionnée{formationIds.length > 1 ? 's' : ''}
                  </small>
                </div>
              )}
            </div>
          ) : (
            <div className="relance-field">
              <p style={{ color: '#999', fontSize: '13px', fontStyle: 'italic' }}>
                <i className="fa-solid fa-info-circle"></i> Aucune formation souhaitée pour ce prospect.
              </p>
            </div>
          )}

          {/* Date */}
          <div className="relance-field">
            <label className="relance-label">
              <i className="fa-regular fa-calendar"></i> Date de relance
              <span className="required-star">*</span>
            </label>
            <input
              type="date"
              className="relance-input"
              value={dateRelance}
              min={new Date().toISOString().split('T')[0]}
              onChange={(e) => setDateRelance(e.target.value)}
            />
          </div>

          {/* Commentaire */}
          <div className="relance-field">
            <label className="relance-label">
              <i className="fa-regular fa-comment"></i> Commentaire
              <span className="optional-tag">optionnel</span>
            </label>
            <textarea
              className="relance-textarea"
              placeholder="Ex : Rappeler pour confirmer son intérêt…"
              rows={3}
              value={commentaire}
              onChange={(e) => setCommentaire(e.target.value)}
            />
          </div>
        </div>

        {/* ── Footer ─────────────────────────────────────────── */}
        <div className="relance-modal-footer">
          <button className="btn-relance-cancel" onClick={onClose} disabled={loading}>
            Annuler
          </button>
          <button className="btn-relance-save" onClick={handleSave} disabled={loading}>
            {loading
              ? <><i className="fa-solid fa-spinner fa-spin"></i> Enregistrement…</>
              : <><i className="fa-solid fa-check"></i> Enregistrer</>
            }
          </button>
        </div>
      </div>
    </div>
  );
}